import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ArticleEditorPanel = ({ article, onSave, onClear, loading }) => {
  const [content, setContent] = useState(article?.content || '');
  const [isEditing, setIsEditing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const textareaRef = useRef(null);

  React.useEffect(() => {
    if (article?.content) {
      setContent(article?.content);
      setIsEditing(false);
    }
  }, [article]);

  const handleContentChange = (e) => {
    setContent(e?.target?.value);
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        ...article,
        content,
        lastModified: new Date()?.toISOString()
      });
    }
    setIsEditing(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard?.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
    }
  };

  const handleClear = () => {
    setContent('');
    setIsEditing(false);
    if (onClear) {
      onClear();
    }
  };

  const wordCount = content?.trim()?.split(/\s+/)?.filter(word => word?.length > 0)?.length;
  const charCount = content?.length;

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-accent rounded-lg flex items-center justify-center">
            <Icon name="Edit3" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Article Editor
            </h2>
            <p className="text-sm text-muted-foreground">
              {article ? 'Edit and refine your generated content' : 'Generated article will appear here'}
            </p>
          </div>
        </div>

        {article && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyToClipboard}
              iconName={copySuccess ? "Check" : "Copy"}
              iconSize={16}
              disabled={!content?.trim()}
              className={copySuccess ? "text-success border-success" : ""}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              iconName="Trash2"
              iconSize={16}
              className="text-muted-foreground hover:text-error"
            >
              Clear
            </Button>
          </div>
        )}
      </div>
      {/* Content Area */}
      <div className="flex-1 flex flex-col p-6">
        {!article && !loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                Ready to Generate
              </h3>
              <p className="text-muted-foreground">
                Configure your article parameters and click "Generate Article" to create AI-powered content that you can edit and refine.
              </p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex-1 space-y-4">
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-muted rounded w-full mb-3"></div>
              <div className="h-4 bg-muted rounded w-5/6 mb-3"></div>
              <div className="h-4 bg-muted rounded w-full mb-6"></div>
              <div className="h-4 bg-muted rounded w-4/5 mb-3"></div>
              <div className="h-4 bg-muted rounded w-full mb-3"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
            </div>
            <div className="text-center py-8">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg animate-pulse mx-auto mb-3"></div>
              <p className="text-sm text-muted-foreground">
                AI is crafting your article...
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 mb-4">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                placeholder="Your generated article content will appear here for editing..."
                className="w-full h-full min-h-[400px] p-4 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground placeholder-muted-foreground font-mono text-sm leading-relaxed"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </div>

            {/* Footer Stats and Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Type" size={14} color="var(--color-muted-foreground)" />
                  <span>{wordCount} words</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
                  <span>{charCount} characters</span>
                </div>
                {article?.lastModified && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                    <span>Modified {new Date(article.lastModified)?.toLocaleTimeString()}</span>
                  </div>
                )}
              </div>

              {isEditing && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  iconName="Save"
                  iconPosition="left"
                  iconSize={16}
                  className="bg-gradient-primary hover:opacity-90"
                >
                  Save Changes
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ArticleEditorPanel;